import styled from 'styled-components';
import React, { useContext, useEffect, useState } from 'react';
import { LabelSelector, ListWrapper, Title, TitleHint } from './components';
import { EditorContext } from '../../../utils/contexts';
import { useSnackbar } from 'notistack';
import SelectInput from '../components/SelectInput';
import { TextField } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import AdsClickIcon from '@mui/icons-material/AdsClick';
import CodeIcon from '@mui/icons-material/Code';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { withEditorPageTracking } from '../../../shared/Tracking';
import '../../../assets/styles/_settings.scss';
import { days } from '../utils';

// Each trigger type maps onto the view_condition values the script understands
const TRIGGER_TYPES = [
  {
    id: 'load',
    label: 'Page load',
    icon: <AccessTimeIcon />,
    defaultCondition: 'immediately',
  },
  {
    id: 'scroll',
    label: 'Scroll',
    icon: <VerticalAlignBottomIcon />,
    defaultCondition: 'scroll-some',
  },
  {
    id: 'exit',
    label: 'Exit intent',
    icon: <ExitToAppIcon />,
    defaultCondition: 'exit-intent',
  },
  {
    id: 'click',
    label: 'Click',
    icon: <AdsClickIcon />,
    defaultCondition: 'onclick',
  },
  {
    id: 'event',
    label: 'Custom event',
    icon: <CodeIcon />,
    defaultCondition: 'event.',
  },
];

const TRIGGER_HINTS = {
  load: 'Show the popup when the page loads, optionally after a delay.',
  scroll: 'Show the popup once the visitor scrolls down the page.',
  exit: 'Show the popup when the visitor is about to leave your site.',
  click: 'Show the popup when a visitor clicks an element on your page, like a "Book a demo" button.',
  event: 'Show the popup when your own code fires an event, like after a signup or a purchase.',
};

const loadDelays = [
  { value: 'immediately', label: 'Immediately' },
  { value: 'wait-5', label: 'After 5 seconds' },
  { value: 'wait-10', label: 'After 10 seconds' },
  { value: 'wait-15', label: 'After 15 seconds' },
  { value: 'wait-20', label: 'After 20 seconds' },
  { value: 'wait-25', label: 'After 25 seconds' },
  { value: 'wait-30', label: 'After 30 seconds' },
  { value: 'wait-45', label: 'After 45 seconds' },
  { value: 'wait-60', label: 'After 60 seconds' },
  { value: 'wait-120', label: 'After 2 minutes' },
];

const scrollDepths = [
  { value: 'scroll-some', label: 'After scrolling a little' },
  { value: 'scroll-middle', label: 'After scrolling to the middle' },
  { value: 'scroll-to-bottom', label: 'After scrolling to the bottom' },
];

const triggerTypeFor = (viewCondition = '') => {
  if (viewCondition.startsWith('event.')) return 'event';
  if (viewCondition === 'onclick') return 'click';
  if (viewCondition === 'exit-intent') return 'exit';
  if (viewCondition.startsWith('scroll')) return 'scroll';
  return 'load';
};

const EVENT_NAME_MAX_LENGTH = 40;
const INVALID_EVENT_CHARS = /[^A-Za-z0-9_-]/;

// Spaces, quotes and other characters would break the generated JS snippet
const toEventName = (value) =>
  value.replace(/[^A-Za-z0-9_-]/g, '_').slice(0, EVENT_NAME_MAX_LENGTH);

const helperTextSx = { fontFamily: 'inherit', fontSize: '1em', mx: 0 };

const isValidSelector = (selector) => {
  try {
    document.createDocumentFragment().querySelector(selector);
    return true;
  } catch (_) {
    return false;
  }
};

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch (_) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    textarea.remove();
  }
};

const TriggerCards = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 8px;
  margin-bottom: 4px;

  /* 3 cards on the first row, 2 wider cards on the second */
  & > :nth-child(-n + 3) {
    grid-column: span 2;
  }
  & > :nth-child(n + 4) {
    grid-column: span 3;
  }
`;

const TriggerCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 10px 4px;
  border: 2px solid #dcdcdc;
  border-radius: 6px;
  background: white;
  color: #8a8a8a;
  font: inherit;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s, color 0.2s;

  svg {
    font-size: 1.6em;
  }

  &:hover {
    border-color: #999;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.07);
  }

  &.active {
    border-color: #27ab9e;
    color: #27ab9e;
    background: #f2fbfa;
  }
`;

const Hint = styled.p`
  margin: 0;
  color: #8a8a8a;
  line-height: 1.4;
`;

const FieldGroup = styled.div`
  display: grid;
  grid-row-gap: 8px;
`;

const FieldLabel = styled.span`
  font-weight: 600;
`;

const Warning = styled.p`
  margin: 0;
  padding: 8px 10px;
  border-radius: 4px;
  background: #fff8e1;
  color: #8a6d00;
  line-height: 1.4;
`;

const CodeBlock = styled.div`
  position: relative;
  padding: 10px 40px 10px 12px;
  border-radius: 6px;
  background: #2b2b2b;
  color: #e6e6e6;
  font-family: Menlo, Monaco, Consolas, 'Courier New', monospace;
  font-size: 0.95em;
  line-height: 1.5;
  white-space: pre-wrap;
  overflow-wrap: anywhere;

  button {
    position: absolute;
    top: 6px;
    right: 6px;
    display: flex;
    padding: 4px;
    border: none;
    border-radius: 4px;
    background: transparent;
    color: #bdbdbd;
    cursor: pointer;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
      color: white;
    }

    svg {
      font-size: 1.1em;
    }
  }
`;

const InfoBox = styled.div`
  display: flex;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 6px;
  background: #f6f6f6;
  color: #636363;
  line-height: 1.4;

  svg {
    flex-shrink: 0;
    font-size: 1.3em;
    color: #8a8a8a;
  }

  p {
    margin: 0;
  }
`;

const CodeSnippet = ({ code }) => {
  const { enqueueSnackbar } = useSnackbar();

  const handleCopy = async () => {
    await copyToClipboard(code);
    enqueueSnackbar('Copied!', { variant: 'success' });
  };

  return (
    <CodeBlock>
      {/* Allow line breaks after . and ( so the code wraps at readable points */}
      {code.split(/([.(])/).map((part, i) =>
        i % 2 ? (
          <React.Fragment key={i}>
            {part}
            <wbr />
          </React.Fragment>
        ) : (
          part
        )
      )}
      <button type="button" title="Copy" onClick={handleCopy}>
        <ContentCopyIcon />
      </button>
    </CodeBlock>
  );
};

const EventTrigger = ({ value, onChange }) => {
  const eventName = value.replace('event.', '');
  // Names saved before validation existed may contain spaces; they keep working
  // as-is, so only warn instead of rewriting them
  const isLegacyName = INVALID_EVENT_CHARS.test(eventName);
  const jsCode = `window.hellobar.trigger.event('${eventName}');`;

  return (
    <>
      <FieldGroup>
        <FieldLabel>Event name</FieldLabel>
        <TextField
          size="small"
          name="event_name"
          required
          placeholder="e.g. book_demo"
          onChange={(e) => onChange(`event.${toEventName(e.target.value)}`)}
          value={eventName}
          variant="outlined"
          helperText="Letters, numbers, _ and - only. Spaces become _"
          FormHelperTextProps={{ sx: helperTextSx }}
          inputProps={{ maxLength: EVENT_NAME_MAX_LENGTH }}
        />
        {isLegacyName && (
          <Warning>
            This name contains spaces or special characters. It still works,
            but if you edit it, update the code on your site too.
          </Warning>
        )}
      </FieldGroup>
      {eventName && (
        <FieldGroup>
          <FieldLabel>Add this code to your site</FieldLabel>
          <Hint>Run it whenever you want the popup to appear:</Hint>
          <CodeSnippet code={jsCode} />
        </FieldGroup>
      )}
    </>
  );
};

const ClickTrigger = ({ value, onChange }) => {
  const selector = value || '';
  const invalid = selector.trim() !== '' && !isValidSelector(selector);

  return (
    <>
      <FieldGroup>
        <FieldLabel>Element to click</FieldLabel>
        <TextField
          size="small"
          name="click_selector"
          required
          placeholder="e.g. #book-demo or .cta-button"
          onChange={(e) => onChange(e.target.value)}
          value={selector}
          variant="outlined"
          error={invalid}
          helperText={
            invalid
              ? "This isn't a valid CSS selector"
              : 'CSS selector of the button or link. Every matching element opens the popup.'
          }
          FormHelperTextProps={{ sx: helperTextSx }}
        />
      </FieldGroup>
      <InfoBox>
        <InfoOutlinedIcon />
        <p>
          <strong>Tip:</strong> give your button an id, like{' '}
          <code>{'<button id="book-demo">'}</code>, and enter{' '}
          <code>#book-demo</code> here. After publishing, click it on your site
          to test.
        </p>
      </InfoBox>
    </>
  );
};

// trigger_open_limit: 0 = unlimited, N = max times one visitor can open the popup (default 1)
const TRIGGER_OPEN_LIMIT_DEFAULT = 1;
const TRIGGER_OPEN_LIMIT_CUSTOM = 'custom';
const TRIGGER_OPEN_LIMIT_PRESETS = [1, 2, 3, 4, 5];
const triggerOpenLimitOptions = [
  ...TRIGGER_OPEN_LIMIT_PRESETS.map((n) => ({
    value: n,
    label: n === 1 ? '1 time' : `${n} times`,
  })),
  { value: TRIGGER_OPEN_LIMIT_CUSTOM, label: 'Custom' },
  { value: 0, label: 'Unlimited' },
];

const TriggerOpenLimitInput = ({ value, onChange }) => {
  const limit = value ?? TRIGGER_OPEN_LIMIT_DEFAULT;
  const [isCustom, setIsCustom] = useState(
    limit !== 0 && !TRIGGER_OPEN_LIMIT_PRESETS.includes(limit)
  );

  const handleSelect = (selected) => {
    if (selected === TRIGGER_OPEN_LIMIT_CUSTOM) {
      setIsCustom(true);
      onChange(limit > 0 ? limit : 1);
    } else {
      setIsCustom(false);
      onChange(selected);
    }
  };

  const handleCustomChange = (e) => {
    const number = parseInt(e.target.value, 10);
    onChange(Number.isNaN(number) || number < 1 ? 1 : number);
  };

  return (
    <FieldGroup>
      <FieldLabel>Show it at most</FieldLabel>
      <SelectInput
        items={triggerOpenLimitOptions}
        value={isCustom ? TRIGGER_OPEN_LIMIT_CUSTOM : limit}
        onChange={handleSelect}
      />
      {isCustom && (
        <>
          <FieldLabel id="trigger-open-limit-label">Number of times</FieldLabel>
          <TextField
            size="small"
            type="number"
            name="trigger_open_limit"
            required
            onChange={handleCustomChange}
            value={limit}
            variant="outlined"
            inputProps={{
              min: 1,
              step: 1,
              'aria-labelledby': 'trigger-open-limit-label',
            }}
          />
        </>
      )}
      <Hint>
        Total times each visitor can see this popup, even after closing it.
      </Hint>
    </FieldGroup>
  );
};

function TriggerStep({ className }) {
  const { settings, updateSettings } = useContext(EditorContext);

  const isInline = settings.type === 'Inline';
  const triggerType = triggerTypeFor(settings.view_condition);
  const isCustomEvent = triggerType === 'event';

  function updateViewCondition(value) {
    updateSettings({ view_condition: value });
    if (value !== 'onclick') {
      updateConditionSelector('');
    }
  }

  const selectTriggerType = (type) => {
    if (type.id === triggerType) return;
    updateViewCondition(type.defaultCondition);
  };

  const updateConditionSelector = (value) => {
    updateSettings({ click_selector: value });
  };

  const updateTriggerOpenLimit = (value) => {
    updateSettings({
      settings: { ...settings.settings, trigger_open_limit: value },
    });
  };

  const updateDismissDuration = (value) => {
    const customSettings = settings.settings;
    customSettings.cookie_settings.duration = value;

    updateSettings({ settings: customSettings });
  };

  const shouldShowDissmissDuration = () => {
    if (isInline) return false;
    // The open limit replaces the dismiss duration for custom event popups
    if (isCustomEvent) return false;

    if (settings.view_condition !== 'onclick') {
      if (settings.type === 'Modal') {
        return true;
      } else if (settings.type === 'Takeover') {
        return true;
      } else {
        return settings.closable;
      }
    } else {
      return false;
    }
  };

  useEffect(() => {
    window.hellobar?.displayPage && window.hellobar?.displayPage('default');
  }, []);

  return (
    <div className={className}>
      <div>
        <Title>Trigger</Title>
        <TitleHint>Choose what makes this popup appear.</TitleHint>
      </div>
      <ListWrapper>
        {isInline && (
          <TitleHint>
            Inline popups are always shown in place on the page.
          </TitleHint>
        )}
        {!isInline && (
          <LabelSelector>
            <span>When does it display?</span>
            <TriggerCards>
              {TRIGGER_TYPES.map((type) => (
                <TriggerCard
                  key={type.id}
                  type="button"
                  className={triggerType === type.id ? 'active' : ''}
                  onClick={() => selectTriggerType(type)}
                >
                  {type.icon}
                  {type.label}
                </TriggerCard>
              ))}
            </TriggerCards>
            <Hint>{TRIGGER_HINTS[triggerType]}</Hint>

            {triggerType === 'load' && (
              <FieldGroup>
                <FieldLabel>Delay</FieldLabel>
                <SelectInput
                  items={loadDelays}
                  value={settings.view_condition}
                  onChange={updateViewCondition}
                />
              </FieldGroup>
            )}
            {triggerType === 'scroll' && (
              <FieldGroup>
                <FieldLabel>Scroll depth</FieldLabel>
                <SelectInput
                  items={scrollDepths}
                  value={settings.view_condition}
                  onChange={updateViewCondition}
                />
              </FieldGroup>
            )}
            {triggerType === 'click' && (
              <ClickTrigger
                value={settings.click_selector}
                onChange={updateConditionSelector}
              />
            )}
            {isCustomEvent && (
              <EventTrigger
                value={settings.view_condition}
                onChange={updateViewCondition}
              />
            )}
            {isCustomEvent && (
              <TriggerOpenLimitInput
                value={settings.settings?.trigger_open_limit}
                onChange={updateTriggerOpenLimit}
              />
            )}
          </LabelSelector>
        )}

        {shouldShowDissmissDuration() && (
          <LabelSelector>
            <span>Don't show again for</span>
            <SelectInput
              items={days}
              value={settings.settings.cookie_settings.duration}
              onChange={updateDismissDuration}
            />
            <Hint>How long to hide the popup after a visitor closes it.</Hint>
          </LabelSelector>
        )}
      </ListWrapper>
    </div>
  );
}

export default styled(withEditorPageTracking(TriggerStep, 'Trigger'))``;
